/**
 * MultiSelectionBox Component
 * Interactive bounding box for multi-selected modules
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 1563-1784)
 * Handles dragging, copying (Alt+drag), and edge expansion
 */

import React, { useState, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { Polygon, CircleMarker, Marker } from 'react-leaflet';
import L from 'leaflet';

export interface MultiSelectionBoxProps {
  /** Bounding box coordinates [TL, TR, BR, BL, TL] */
  bounds: [number, number][];

  /** Callback when box is moved (drag) */
  onMove: (delta: { lat: number; lng: number }) => void;

  /** Callback when box is copied (Alt+drag) */
  onCopy: (delta: { lat: number; lng: number }) => void;

  /** Optional: Callback when edge is expanded (for module arrays) */
  onExpandEdge?: (edge: 'top' | 'bottom' | 'left' | 'right', distance: number) => void;
}

/**
 * MultiSelectionBox component
 *
 * Features:
 * - Drag to move selection
 * - Alt/Option + Drag to copy selection (green visual feedback)
 * - Edge handles for array expansion (Row/Col labels)
 * - Prevents map panning during interaction
 */
export function MultiSelectionBox({
  bounds,
  onMove,
  onCopy,
  onExpandEdge,
}: MultiSelectionBoxProps) {
  const map = useMap();
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState<{ lat: number; lng: number } | null>(null);
  const [isOptionPressed, setIsOptionPressed] = useState(false);
  const [draggingEdge, setDraggingEdge] = useState<string | null>(null);
  const [edgeStartPos, setEdgeStartPos] = useState<{ lat: number; lng: number } | null>(null);

  // Track Alt/Option key for copy mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt' || e.key === 'Option') {
        setIsOptionPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt' || e.key === 'Option') {
        setIsOptionPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Main box drag handlers
  const handleMouseDown = (e: any) => {
    L.DomEvent.stopPropagation(e);
    setIsDragging(true);
    setStartPos({ lat: e.latlng.lat, lng: e.latlng.lng });
    map.dragging.disable();
  };

  const handleMouseMove = (e: any) => {
    if (!isDragging || !startPos) return;
    L.DomEvent.stopPropagation(e);

    const delta = {
      lat: e.latlng.lat - startPos.lat,
      lng: e.latlng.lng - startPos.lng,
    };

    onMove(delta);
    setStartPos({ lat: e.latlng.lat, lng: e.latlng.lng });
  };

  const handleMouseUp = (e: any) => {
    if (!isDragging) return;
    L.DomEvent.stopPropagation(e);

    if (isOptionPressed && startPos) {
      // Copy instead of move
      const delta = {
        lat: e.latlng.lat - startPos.lat,
        lng: e.latlng.lng - startPos.lng,
      };
      onCopy(delta);
    }

    setIsDragging(false);
    setStartPos(null);
    // Note: Don't enable dragging here - let SpacebarPanController manage it
  };

  useEffect(() => {
    if (isDragging) {
      map.on('mousemove', handleMouseMove);
      map.on('mouseup', handleMouseUp);
    } else {
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
    }

    return () => {
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
    };
  }, [isDragging, startPos, isOptionPressed]);

  // Edge handle drag handlers
  const handleEdgeMouseDown = (edge: string, e: any) => {
    L.DomEvent.stopPropagation(e);
    setDraggingEdge(edge);
    setEdgeStartPos({ lat: e.latlng.lat, lng: e.latlng.lng });
    map.dragging.disable();
  };

  const handleEdgeMouseMove = (e: any) => {
    if (!draggingEdge || !edgeStartPos || !onExpandEdge) return;
    L.DomEvent.stopPropagation(e);

    // Calculate distance moved
    const deltaLat = e.latlng.lat - edgeStartPos.lat;
    const deltaLng = e.latlng.lng - edgeStartPos.lng;

    // Calculate distance in meters (approximate)
    const latDegPerMeter = 1 / 111320;
    const lngDegPerMeter = 1 / (111320 * Math.cos((edgeStartPos.lat * Math.PI) / 180));

    const distanceMeters = Math.sqrt(
      Math.pow(deltaLat / latDegPerMeter, 2) + Math.pow(deltaLng / lngDegPerMeter, 2)
    );

    // Determine direction (outward is positive)
    let directedDistance = distanceMeters;
    // This is simplified - proper implementation would check if moving toward or away from center

    onExpandEdge(draggingEdge as any, directedDistance);
  };

  const handleEdgeMouseUp = (e: any) => {
    if (!draggingEdge) return;
    L.DomEvent.stopPropagation(e);

    setDraggingEdge(null);
    setEdgeStartPos(null);
    // Note: Don't enable dragging here - let SpacebarPanController manage it
  };

  useEffect(() => {
    if (draggingEdge) {
      map.on('mousemove', handleEdgeMouseMove);
      map.on('mouseup', handleEdgeMouseUp);
    } else {
      map.off('mousemove', handleEdgeMouseMove);
      map.off('mouseup', handleEdgeMouseUp);
    }

    return () => {
      map.off('mousemove', handleEdgeMouseMove);
      map.off('mouseup', handleEdgeMouseUp);
    };
  }, [draggingEdge, edgeStartPos]);

  // Calculate edge midpoints for handles
  const edgeMidpoints = {
    top: [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2] as [
      number,
      number
    ],
    right: [(bounds[1][0] + bounds[2][0]) / 2, (bounds[1][1] + bounds[2][1]) / 2] as [
      number,
      number
    ],
    bottom: [(bounds[2][0] + bounds[3][0]) / 2, (bounds[2][1] + bounds[3][1]) / 2] as [
      number,
      number
    ],
    left: [(bounds[3][0] + bounds[0][0]) / 2, (bounds[3][1] + bounds[0][1]) / 2] as [
      number,
      number
    ],
  };

  return (
    <>
      <Polygon
        positions={bounds}
        pathOptions={{
          color: isOptionPressed ? '#22c55e' : '#EBFD03',
          fillColor: isOptionPressed ? '#22c55e' : '#EBFD03',
          fillOpacity: 0.1,
          weight: 3,
          dashArray: '8, 4',
        }}
        eventHandlers={{
          mousedown: handleMouseDown,
        }}
      />

      {/* Edge handles for array expansion */}
      {onExpandEdge &&
        Object.entries(edgeMidpoints).map(([edge, position]) => {
          // Determine label based on edge
          const isVertical = edge === 'top' || edge === 'bottom';
          const label = isVertical ? 'Row' : 'Col';

          return (
            <React.Fragment key={`edge-handle-${edge}`}>
              {/* Circular handle */}
              <CircleMarker
                center={position}
                // @ts-ignore - react-leaflet v5 supports radius but v2 types don't
                radius={8}
                pathOptions={{
                  color: draggingEdge === edge ? '#16a34a' : '#2563eb',
                  fillColor: draggingEdge === edge ? '#22c55e' : '#3b82f6',
                  fillOpacity: 0.9,
                  weight: 2,
                }}
                eventHandlers={{
                  mousedown: (e: any) => handleEdgeMouseDown(edge, e),
                }}
              />
              {/* Text label */}
              <Marker
                position={position}
                // @ts-ignore - react-leaflet v5 supports icon but v2 types don't
                icon={L.divIcon({
                  className: 'edge-label',
                  html: `<div style="
                  background: ${draggingEdge === edge ? '#22c55e' : '#3b82f6'};
                  color: white;
                  padding: 2px 6px;
                  border-radius: 4px;
                  font-size: 10px;
                  font-weight: bold;
                  white-space: nowrap;
                  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                  transform: translate(${edge === 'left' ? '-120%' : edge === 'right' ? '20%' : '-50%'}, ${edge === 'top' ? '-180%' : edge === 'bottom' ? '80%' : '-50%'});
                ">${label}</div>`,
                  iconSize: [0, 0],
                  iconAnchor: [0, 0],
                })}
              />
            </React.Fragment>
          );
        })}
    </>
  );
}
